import { useState, useRef, useLayoutEffect, useCallback, useEffect } from 'react';
import { useMutation, useInfiniteQuery, useQueryClient } from '@tanstack/react-query';
import { useOutletContext, useParams, useNavigate } from 'react-router-dom';
import { Send, Loader2, ArrowDown, Trash2, Plus, Eye } from 'lucide-react';
import toast from 'react-hot-toast';
import { MessageBubble } from '@/components/Chat/MessageBubble';
import { MessageContextMenu } from '@/components/Chat/MessageContextMenu';
import { ThinkingAnimation } from '@/components/Chat/ThinkingAnimation';
import { SessionDropdown } from '@/components/Chat/SessionDropdown';
import { useAuthStore } from '@/stores/useAuthStore';
import type {
  Message,
  ChatResponse,
  PaginatedMessages,
  PaginatedSessions,
  ChatSession,
} from '@/types/chat';
import { api } from '@/lib/axios';
import styles from './Chat.module.css';

type LayoutContext = { setHeaderSlot: (node: React.ReactNode) => void };

export function Chat() {
  const queryClient = useQueryClient();
  const { setHeaderSlot } = useOutletContext<LayoutContext>();
  const { sessionId: urlSessionId } = useParams<{ sessionId?: string }>();
  const navigate = useNavigate();

  // ─── Auth / admin ────────────────────────────────────────────────────────
  const isAdmin = useAuthStore((s) => s.isAdmin());
  const currentUserEmail = useAuthStore((s) => s.user?.email);

  // ─── State ───────────────────────────────────────────────────────────────
  const [input, setInput] = useState('');
  const [localMessages, setLocalMessages] = useState<Message[]>([]);
  const [activeSessionId, setActiveSessionId] = useState<string | null>(urlSessionId ?? null);

  // Textarea ref for auto-resize
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Scroll / pagination guards
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const isInitialMount = useRef(true);
  const lastScrollTop = useRef(0);
  const canLoadMore = useRef(false);
  const [showScrollButton, setShowScrollButton] = useState(false);

  // ─── Sessions Query ───────────────────────────────────────────────────────
  const {
    data: sessionsData,
    fetchNextPage: fetchMoreSessions,
    hasNextPage: hasMoreSessions,
    isLoading: sessionsLoading,
  } = useInfiniteQuery({
    queryKey: ['chat-sessions'],
    queryFn: async ({ pageParam = 1 }) => {
      const res = await api.get<PaginatedSessions>('/api/chat/sessions/', {
        params: { page: pageParam },
      });
      return res.data;
    },
    initialPageParam: 1,
    getNextPageParam: (lastPage) => lastPage.next,
    staleTime: 30_000,
    refetchOnWindowFocus: false,
  });

  const allSessions: ChatSession[] = sessionsData?.pages.flatMap((p) => p.results) ?? [];

  const activeSession = allSessions.find((s) => s.id === activeSessionId) ?? null;
  const isViewingForeignSession =
    isAdmin &&
    !!activeSession?.owner_email &&
    activeSession.owner_email !== currentUserEmail;

  // Navigate + set active session in one call
  const switchSession = useCallback((id: string | null) => {
    setActiveSessionId(id);
    if (id) {
      navigate(`/dashboard/chat/${id}`, { replace: true });
    } else {
      navigate('/dashboard/chat', { replace: true });
    }
  }, [navigate]);

  // Auto-select most recent session (or create one) on first load
  const sessionsInitialized = useRef(false);
  if (!sessionsInitialized.current && !sessionsLoading && sessionsData) {
    sessionsInitialized.current = true;
    if (allSessions.length > 0 && activeSessionId === null) {
      switchSession(allSessions[0].id);
    }
    // If no sessions, the createSessionMutation fires on mount below
  }

  // ─── Create Session ───────────────────────────────────────────────────────
  const createSessionMutation = useMutation<ChatSession, Error, string | void>({
    mutationFn: async (title = 'New Chat') => {
      const res = await api.post<ChatSession>('/api/chat/sessions/', { title });
      return res.data;
    },
    onSuccess: (session) => {
      queryClient.invalidateQueries({ queryKey: ['chat-sessions'] });
      switchSession(session.id);
      setLocalMessages([]);
      isInitialMount.current = true;
      canLoadMore.current = false;
    },
  });

  // Create a session if none exist after loading
  const autoCreateRef = useRef(false);
  if (
    !sessionsLoading &&
    sessionsData &&
    allSessions.length === 0 &&
    !autoCreateRef.current &&
    !createSessionMutation.isPending
  ) {
    autoCreateRef.current = true;
    createSessionMutation.mutate('New Chat');
  }

  // ─── Messages Query (per session) ────────────────────────────────────────
  const {
    data: messagesData,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading: messagesLoading,
  } = useInfiniteQuery({
    queryKey: ['chat-messages', activeSessionId],
    queryFn: async ({ pageParam = 1 }) => {
      if (!activeSessionId) throw new Error('No session');
      const res = await api.get<PaginatedMessages>(
        `/api/chat/sessions/${activeSessionId}/messages/`,
        {
          params: { page: pageParam },
        },
      );
      return res.data;
    },
    initialPageParam: 1,
    getNextPageParam: (lastPage) => lastPage.next,
    enabled: activeSessionId !== null,
    staleTime: 0,
    refetchOnWindowFocus: false,
  });

  // ─── Send Message ─────────────────────────────────────────────────────────
  const sendMessageMutation = useMutation({
    mutationFn: async (content: string) => {
      const res = await api.post<ChatResponse>(`/api/chat/sessions/${activeSessionId}/messages/`, {
        messages: [{ role: 'user', content }],
      });
      return res.data;
    },
    onSuccess: (data) => {
      const botMsg: Message = {
        role: 'assistant',
        content: data.message,
        options: data.options,
        quotation: data.quotation,
        pendingQuestion: data.pending_question ?? null,
        pendingQuestionOptions: data.pending_question_options ?? [],
        created_at: new Date().toISOString(),
      };
      setLocalMessages((prev) => [...prev, botMsg]);
      scrollToBottom();
      queryClient.invalidateQueries({ queryKey: ['chat-messages', activeSessionId] }).then(() => {
        setTimeout(() => setLocalMessages([]), 100);
      });
      // Update session title in list (message count changed)
      queryClient.invalidateQueries({ queryKey: ['chat-sessions'] });
    },
    onError: () => {
      setLocalMessages((prev) => [
        ...prev,
        {
          role: 'assistant',
          content: 'I am sorry, there was an error. Please try again.',
        },
      ]);
    },
  });

  // ─── Clear Messages ───────────────────────────────────────────────────────
  const clearMessagesMutation = useMutation({
    mutationFn: async () => {
      if (!activeSessionId) return;
      await api.delete(`/api/chat/sessions/${activeSessionId}/messages/`);
    },
    onSuccess: () => {
      setLocalMessages([]);
      isInitialMount.current = true;
      canLoadMore.current = false;
      queryClient.resetQueries({ queryKey: ['chat-messages', activeSessionId] });
      queryClient.invalidateQueries({ queryKey: ['chat-sessions'] });
    },
  });

  // ─── Delete Session ───────────────────────────────────────────────────────
  const deleteSessionMutation = useMutation({
    mutationFn: async (sessionId: string) => {
      await api.delete(`/api/chat/sessions/${sessionId}/`);
    },
    onSuccess: (_, deletedId) => {
      queryClient.invalidateQueries({ queryKey: ['chat-sessions'] });
      queryClient.removeQueries({ queryKey: ['chat-messages', deletedId] });
      if (activeSessionId === deletedId) {
        // Switch to next available session or create new
        const remaining = allSessions.filter((s) => s.id !== deletedId);
        if (remaining.length > 0) {
          switchSession(remaining[0].id);
        } else {
          switchSession(null);
          autoCreateRef.current = false; // allow auto-create
        }
      }
    },
  });

  // ─── Rename Session ───────────────────────────────────────────────────────
  const renameSessionMutation = useMutation({
    mutationFn: async ({ id, title }: { id: string; title: string }) => {
      const res = await api.patch(`/api/chat/sessions/${id}/`, { title });
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['chat-sessions'] });
    },
  });

  // ─── Fork Session ─────────────────────────────────────────────────────────
  const [contextMenu, setContextMenu] = useState<{
    x: number;
    y: number;
    messageId: string;
  } | null>(null);

  const forkSessionMutation = useMutation({
    mutationFn: async ({ messageId }: { messageId: string }) => {
      const res = await api.post<ChatSession>(
        `/api/chat/sessions/${activeSessionId}/fork/`,
        { message_id: messageId },
      );
      return res.data;
    },
    onSuccess: (session) => {
      queryClient.invalidateQueries({ queryKey: ['chat-sessions'] });
      switchSession(session.id);
      setLocalMessages([]);
      isInitialMount.current = true;
      canLoadMore.current = false;
      toast.success('New chat created from this point', { duration: 3000 });
    },
    onError: (err: Error) => {
      toast.error(`Could not fork chat: ${err.message}`, { duration: 8000 });
    },
  });

  const handleMessageContextMenu = useCallback(
    (e: React.MouseEvent, messageId: string) => {
      setContextMenu({ x: e.clientX, y: e.clientY, messageId });
    },
    [],
  );

  // ─── Switch Session (called from SessionDropdown) ─────────────────────────
  const handleSwitchSession = useCallback((id: string) => {
    switchSession(id);
    setLocalMessages([]);
    isInitialMount.current = true;
    canLoadMore.current = false;
  }, [switchSession]);

  // ─── Construct Display Messages ───────────────────────────────────────────
  const historyMessages: Message[] =
    messagesData?.pages
      .slice()
      .reverse()
      .flatMap((page) =>
        page.results
          .slice()
          .reverse()
          .map((m) => ({
            id: m.id,
            role: m.role,
            content: m.content,
            created_at: m.created_at,
            options: m.options || [],
            quotation: m.quotation,
            pendingQuestion: m.pending_question ?? null,
            pendingQuestionOptions: m.pending_question_options ?? [],
          })),
      ) ?? [];

  const allMessages: Message[] = [...historyMessages];
  const newestHistoryTime =
    historyMessages.length > 0
      ? new Date(historyMessages[historyMessages.length - 1].created_at!).getTime()
      : 0;

  localMessages.forEach((localMsg) => {
    const localTime = localMsg.created_at ? new Date(localMsg.created_at).getTime() : Date.now();
    const existsById = localMsg.id && allMessages.some((m) => m.id === localMsg.id);
    if (!existsById && localTime >= newestHistoryTime) {
      allMessages.push(localMsg);
    }
  });

  // ─── Inject header controls into DashboardLayout header ──────────────────
  // Intentionally omitting mutation objects (createSessionMutation, clearMessagesMutation,
  // deleteSessionMutation, renameSessionMutation) and stable callbacks (fetchMoreSessions,
  // setHeaderSlot, switchSession) from the dep array — they change identity on every render
  // but the only values that actually affect the rendered UI are the ones listed below.
  /* eslint-disable react-hooks/exhaustive-deps */
  useEffect(() => {
    setHeaderSlot(
      <>
        <SessionDropdown
          sessions={allSessions}
          activeSessionId={activeSessionId}
          isLoading={sessionsLoading}
          hasNextPage={hasMoreSessions ?? false}
          onSelectSession={handleSwitchSession}
          onNewSession={() => createSessionMutation.mutate('New Chat')}
          onDeleteSession={(id) => deleteSessionMutation.mutate(id)}
          onRenameSession={(id, title) => renameSessionMutation.mutate({ id, title })}
          onLoadMore={() => fetchMoreSessions()}
        />
        <button
          onClick={() => createSessionMutation.mutate('New Chat')}
          disabled={createSessionMutation.isPending}
          className={styles.headerBtn}
          title="New chat"
        >
          {createSessionMutation.isPending ? (
            <Loader2 size={14} className={styles.spinning} />
          ) : (
            <Plus size={14} />
          )}
          <span className={styles.headerBtnLabel}>New</span>
        </button>
        <button
          onClick={() => clearMessagesMutation.mutate()}
          disabled={clearMessagesMutation.isPending || allMessages.length === 0}
          className={`${styles.headerBtn} ${styles.dangerBtn}`}
          title="Clear messages"
        >
          {clearMessagesMutation.isPending ? (
            <Loader2 size={14} className={styles.spinning} />
          ) : (
            <Trash2 size={14} />
          )}
        </button>
      </>,
    );
    return () => setHeaderSlot(null);
  }, [
    sessionsData,
    activeSessionId,
    sessionsLoading,
    hasMoreSessions,
    createSessionMutation.isPending,
    clearMessagesMutation.isPending,
    allMessages.length,
  ]);
  /* eslint-enable react-hooks/exhaustive-deps */

  // ─── Scroll Logic ─────────────────────────────────────────────────────────
  const scrollToBottom = (smooth = true) => {
    messagesEndRef.current?.scrollIntoView({ behavior: smooth ? 'smooth' : 'auto' });
  };

  const handleScroll = () => {
    if (!messagesContainerRef.current) return;
    const { scrollTop, scrollHeight, clientHeight } = messagesContainerRef.current;
    const isScrollingUp = scrollTop < lastScrollTop.current;
    setShowScrollButton(scrollHeight - scrollTop - clientHeight > 300);

    if (
      scrollTop < 50 &&
      isScrollingUp &&
      hasNextPage &&
      !isFetchingNextPage &&
      scrollHeight > clientHeight &&
      canLoadMore.current
    ) {
      const currentHeight = scrollHeight;
      fetchNextPage().then(() => {
        if (messagesContainerRef.current) {
          const newHeight = messagesContainerRef.current.scrollHeight;
          messagesContainerRef.current.scrollTop = newHeight - currentHeight;
        }
      });
    }
    lastScrollTop.current = scrollTop;
  };

  useLayoutEffect(() => {
    if (isInitialMount.current && allMessages.length > 0) {
      scrollToBottom(false);
      isInitialMount.current = false;
      setTimeout(() => {
        canLoadMore.current = true;
      }, 300);
    } else if (sendMessageMutation.isPending) {
      scrollToBottom(true);
    }
  }, [allMessages.length, sendMessageMutation.isPending]);

  // ─── Textarea auto-resize ─────────────────────────────────────────────────
  useEffect(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${el.scrollHeight}px`;
  }, [input]);

  // ─── Send a message programmatically (used by option buttons) ────────────
  const sendMessage = useCallback(
    (content: string) => {
      if (!content.trim() || sendMessageMutation.isPending || !activeSessionId) return;
      const userMsg: Message = { role: 'user', content, created_at: new Date().toISOString() };
      setLocalMessages((prev) => [...prev, userMsg]);
      sendMessageMutation.mutate(content);
    },
    // sendMessageMutation identity is stable across renders; activeSessionId is the real dep
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [activeSessionId, sendMessageMutation.isPending],
  );

  // ─── Form Submit ──────────────────────────────────────────────────────────
  const handleSubmit = (e?: React.FormEvent | React.KeyboardEvent) => {
    e?.preventDefault();
    if (!input.trim() || sendMessageMutation.isPending || !activeSessionId) return;
    const content = input;
    setInput('');
    // Reset textarea height after clearing
    if (textareaRef.current) textareaRef.current.style.height = 'auto';
    sendMessage(content);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    // Only Ctrl + Enter sends the message.
    // Plain Enter and Shift + Enter are allowed to create new lines (standard textarea behavior).
    if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
      e.preventDefault();
      handleSubmit(e);
    }
  };

  // ─── UI States ────────────────────────────────────────────────────────────
  const isInitialLoading = messagesLoading && activeSessionId !== null;
  const showWelcomeMessage =
    allMessages.length === 0 &&
    !isFetchingNextPage &&
    !isInitialLoading &&
    !sendMessageMutation.isPending;

  return (
    <div className={styles.container}>
      {/* Messages */}
      <div className={styles.messagesContainer} ref={messagesContainerRef} onScroll={handleScroll}>
        {/* Initial Loading Skeleton */}
        {isInitialLoading && (
          <div className={styles.skeletonContainer}>
            <div className={styles.skeletonRow}>
              <div className={`${styles.skeletonAvatar} ${styles.bot}`} />
              <div className={`${styles.skeletonBubble} ${styles.bot}`} />
            </div>
            <div className={`${styles.skeletonRow} ${styles.reverse}`}>
              <div className={`${styles.skeletonAvatar} ${styles.user}`} />
              <div className={`${styles.skeletonBubble} ${styles.user}`} />
            </div>
            <div className={styles.skeletonRow}>
              <div className={`${styles.skeletonAvatar} ${styles.bot}`} />
              <div className={styles.skeletonCards}>
                <div className={styles.skeletonCardsBubble} />
                <div className={styles.skeletonCardsRow}>
                  <div className={styles.skeletonCard} />
                  <div className={styles.skeletonCard} />
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Loader for older messages */}
        {!isInitialLoading && isFetchingNextPage && (
          <div className={styles.pageLoader}>
            <Loader2 size={20} className={`${styles.spinning} ${styles.loaderIcon}`} />
          </div>
        )}

        {/* Welcome Message */}
        {showWelcomeMessage && (
          <MessageBubble
            message={{
              role: 'assistant',
              content:
                "Hi! I'm your event assistant. Tell me what you're looking for — type of event, number of guests, and location — and I'll build you a professional quotation.",
            }}
          />
        )}

        {allMessages.map((m, i) => (
          <MessageBubble
            key={m.id ?? i}
            message={m}
            onSendMessage={sendMessage}
            isSending={sendMessageMutation.isPending}
            onMessageContextMenu={m.id ? handleMessageContextMenu : undefined}
          />
        ))}

        {/* Staged Thinking Animation */}
        <ThinkingAnimation isPending={sendMessageMutation.isPending} />

        <div ref={messagesEndRef} />
      </div>

      {/* Scroll to bottom button */}
      {showScrollButton && (
        <div className={styles.scrollButtonWrapper}>
          <button
            onClick={() => scrollToBottom()}
            className={styles.scrollButton}
            aria-label="Scroll to bottom"
          >
            <ArrowDown size={18} />
          </button>
        </div>
      )}

      {/* Read-only banner for admin viewing foreign sessions */}
      {isViewingForeignSession && (
        <div className={styles.readOnlyBanner}>
          <Eye size={14} />
          <span>
            Viewing <strong>{activeSession?.owner_name || activeSession?.owner_email}</strong>'s session — read-only
          </span>
        </div>
      )}

      {/* Message right-click context menu */}
      {contextMenu && (
        <MessageContextMenu
          x={contextMenu.x}
          y={contextMenu.y}
          isFetching={forkSessionMutation.isPending}
          onForkFromHere={() =>
            forkSessionMutation.mutate({ messageId: contextMenu.messageId })
          }
          onClose={() => setContextMenu(null)}
        />
      )}

      {/* Input */}
      <div className={styles.inputArea}>
        <form onSubmit={handleSubmit} className={styles.inputForm}>
          <textarea
            ref={textareaRef}
            rows={1}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={
              isViewingForeignSession
                ? 'Read-only — you are viewing another user\'s session'
                : 'Describe your event (type · guests · location)...'
            }
            className={styles.inputField}
            disabled={sendMessageMutation.isPending || !activeSessionId || isViewingForeignSession}
            autoFocus={!isViewingForeignSession}
          />
          <button
            type="submit"
            disabled={!input.trim() || sendMessageMutation.isPending || !activeSessionId || isViewingForeignSession}
            className={styles.sendButton}
          >
            <Send size={18} />
          </button>
        </form>
        <p className={styles.disclaimer}>
          Ebombo AI can make mistakes. Please confirm availability directly with providers.
        </p>
      </div>
    </div>
  );
}
